import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit,
  TemplateRef,
} from '@angular/core';
import {
  MatAutocompleteModule,
  MatAutocompleteSelectedEvent,
} from '@angular/material/autocomplete';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatAutocompleteOptionsScrollDirective } from './mat-autocomplete-options-scroll.directive';

import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { FlexLayoutModule } from '@ngbracket/ngx-layout';
import { Observable, BehaviorSubject, Subject } from 'rxjs';
import { startWith, debounceTime, switchMap, tap, takeWhile, scan, distinctUntilChanged } from 'rxjs/operators';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';


type SelectAutoComplete = {
  disabled?: boolean;
  title: string;
  info?: string;
  placeholder: string;
  hint?: string;
  row: number;
  section?: number;
  appearance: 'outline' | 'fill' | 'standard';
};

@Component({
  standalone: true,
  selector: 'select-auto-complete',
  templateUrl: './select-auto-complete.component.html',
  styleUrls: ['./select-auto-complete.component.scss'],
  imports: [
    CommonModule,
    FlexLayoutModule,
    MatSelectModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatTooltipModule,
    MatAutocompleteModule,
    MatAutocompleteOptionsScrollDirective,
    MatInputModule,
    MatIconModule,
    MatProgressSpinnerModule
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SelectAutoCompleteComponent implements OnInit {
  @Input() labels: SelectAutoComplete;
  @Input() displayFn: any;
  private _value: string;
  @Input() set value(value: string) {
    if (value) {
      this._value = value;
      this.search.patchValue(this._value, { emitEvent: false, onlySelf: true });
    }
  }
  get value(): string {
    return this._value;
  }
  private _isLoading: boolean = false;
  @Input() set isLoading(value: boolean) {
    this._isLoading = value ?? false;
    if (!value && this.filterValue !== this.search.value) {
      this._isLoading = value;
      this.startSearch.next(this.search.value);
    }
  }
  get isLoading(): boolean {
    return this._isLoading;
  }

  @Input() isRequired: boolean = false;
  @Input() columnToDisplay: any;
  @Input() searchInput: (value: any, page: number) => Observable<any>;
  @Input() selectionChange: (value: any, i?: number, j?: number) => void;
  @Input() template: TemplateRef<any>;
  private _markAllAsTouched: boolean;
  @Input() set markAllAsTouched(value: boolean) {
    if (value) {
      this._markAllAsTouched = value
      this.search.markAllAsTouched();
    }
  }
  get markAllAsTouched(): boolean {
    return this._markAllAsTouched;
  }

  filterValue: string = '';
  tooltip: any;
  startSearch = new BehaviorSubject<any>('');
  startSearch$ = this.startSearch.asObservable();
  next$ = new Subject();
  search = new FormControl('');
  loading = false;

  data$ = this.startSearch$.pipe(
    startWith(''),
    debounceTime(200),
    distinctUntilChanged(),
    switchMap((filter) => {
      let currentPage = 1;
      this.filterValue = filter;
      return this.next$.pipe(
        startWith(currentPage),
        tap(() => this.loading = true),
        switchMap((_) => this.searchInput(String(filter), currentPage)),
        tap(() => this.loading = false),
        tap(() => currentPage++),
        takeWhile((x) => x.length > 0),
        scan((allData: any, newDate: any) => allData.concat(newDate), [])
      );
    }),
  );

  constructor() {
    this.search.valueChanges.pipe(
      debounceTime(450),
      distinctUntilChanged()
    ).subscribe((x) => {
      if (typeof (x) != typeof ('') || x.length < 1 || this.isLoading) {
        return;
      }
      this.startSearch.next(x);
    });
  }

  ngOnChanges() {
    // Reaplica os validadores quando a propriedade IsRequired mudar
    this.updateValidators();
  }

  ngOnInit() {
    // Condicionalmente adicione o validador required
    this.updateValidators()
  }

  ngAfterViewInit() {
    if (this.labels.disabled) {
      this.search.disable();
    }
  }

  onScroll() {
    if (this.isLoading) {
      return;
    }
    this.next$.next('');
  }

  onSelectionChanged(event: MatAutocompleteSelectedEvent) {
    this.startSearch.next('');
    this.selectionChange(
      event.option.value,
      this.labels.row,
      this.labels.section
    );
    this.tooltip = event.option.value;
    if (Array.isArray(event.option.value)) {
      this.search.patchValue(
        event.option.value[event.option.value.length - 1][this.columnToDisplay],
        { emitEvent: false, onlySelf: true }
      );
    } else {
      this.search.patchValue(event.option.value[this.columnToDisplay], {
        emitEvent: false,
        onlySelf: true,
      });
    }
  }

  onPanelOpen() {
    this.startSearch.next('');
  }

  returnColumnTooltip(): string {
    if (Array.isArray(this.tooltip)) {
      return this.tooltip.map((m: any) => m.value).join(' > ');
    } else if (typeof this.tooltip === 'string') {
      return this.tooltip;
    }

    return '';
  }

  private updateValidators() {
    // Condicionalmente adicione ou remova o validador required
    if (this.isRequired) {
      this.search.setValidators([Validators.required]);
    } else {
      this.search.clearValidators();
    }
    // Atualize o FormControl para refletir as mudanças nos validadores
    this.search.updateValueAndValidity();
  }
}
